import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Column } from '@appkit4/react-components/table';
import { Button } from '@appkit4/react-components/button';
import { Search } from '@appkit4/react-components/search';
import './FillPDFSettings.scss';
import { AppRoutes } from '../../../Constants';
import {
    useGetV1DocumentTypeByProcessTypeProcessTypeId,
    usePostV1DocumentTypeDeactivate,
} from '../../../api/generated/endpoint';
import useToast from '../../../helpers/useToast';
import {
    DocumentTypeResponse,
    DeactivateDocumentTypeRequest,
} from '../../../api/generated/models';
import { DeleteModal } from '../../../components/shared/deleteModal';
import PDFTemplateModal from '../../../components/shared/pdfTemplateModal/PdfTemplateModal';
import CreateDocTypeModal from '../../../components/shared/createDocTypeModal/CreateDocTypeModal';
import axios from 'axios';
import { useAuth } from 'react-oidc-context';

interface DocumentType {
    documentTypeName: string;
    documentTypeId: number;
}

const FillPDFSettings: React.FC = () => {
    const [availableDocTypes, setAvailableDocTypes] = useState<
        DocumentTypeResponse[]
    >([]);
    const [searchTerm, setSearchTerm] = useState<string>('');

    const pageSize = 7;
    const navigate = useNavigate();
    const showToast = useToast();
    const [isPdfModalVisible, setIsPdfModalVisible] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState<number | null>(
        null
    );
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [createDocTypeModalVisible, setCreateDocTypeModalVisible] =
        useState(false);
    const [documentTypeToDelete, setDocumentTypeToDelete] = useState<
        number | null
    >(null);

    const auth = useAuth();

    const { data: documentTypesData, refetch: refetchDocTypes } =
        useGetV1DocumentTypeByProcessTypeProcessTypeId(2);

    const mutateDeactivateTemplate = usePostV1DocumentTypeDeactivate();

    // Refetch logic for document types
    const fetchDocumentTypes = async () => {
        try {
            if (refetchDocTypes) {
                const result = await refetchDocTypes();
                if (result?.data) {
                    setAvailableDocTypes(result.data);
                }
            } else if (documentTypesData) {
                setAvailableDocTypes(documentTypesData);
            }
        } catch (error) {
            console.error('Error fetching document types:', error);
        }
    };

    useEffect(() => {
        fetchDocumentTypes();
    }, []);

    const filteredDocTypes = availableDocTypes.filter((docType) =>
        (docType.documentTypeName ?? '')
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
    );

    const documentTypeToDeleteName =
        documentTypesData?.find(
            (doc) => doc.documentTypeId === documentTypeToDelete
        )?.documentTypeName || '';

    const navigateToSettings = (row: DocumentType) => {
        navigate(
            `${AppRoutes.FillPDFDocTypeSettings}?docType=${row.documentTypeName}&docTypeId=${row.documentTypeId}&processTypeId=2`
        );
    };

    const renderCell = (row: DocumentType, field: string) => (
        <span
            role="button"
            onClick={() => navigateToSettings(row)}
            aria-label="Settings"
            style={
                field === 'documentTypeId'
                    ? { marginLeft: '50px', textAlign: 'center' }
                    : {}
            }
        >
            {row[field as keyof DocumentType]}
        </span>
    );

    const handleExpand = (row: any) => {
        setSelectedDocument(row.documentTypeId);
        setIsPdfModalVisible(true);
    };

    const handleDeleteTemplate = (id: number) => {
        setDocumentTypeToDelete(id);
        setDeleteModalVisible(true);
    };

    const handleConfirmDelete = async () => {
        if (documentTypeToDelete !== null) {
            try {
                await mutateDeactivateTemplate.mutateAsync({
                    data: {
                        documentTypeId: documentTypeToDelete,
                        deactivationReason: 'User requested deletion',
                    } as DeactivateDocumentTypeRequest,
                });
                showToast(
                    'Success',
                    'Document type deleted successfully',
                    'success'
                );

                setDeleteModalVisible(false);
                setDocumentTypeToDelete(null);

                fetchDocumentTypes();
            } catch (error: any) {
                showToast(
                    'Error',
                    'Failed to delete document type. Please try again.',
                    'error'
                );
            }
        }
    };

    const handleCancelDelete = () => {
        setDeleteModalVisible(false);
        setDocumentTypeToDelete(null);
    };

    const handleCreateDocType = () => {
        setCreateDocTypeModalVisible(true);
    };

    const handleCloseCreateDocTypeModal = () => {
        setCreateDocTypeModalVisible(false);
    };

    const handleConfirmCreateDocTypeModal = async (
        documentTypeName: string,
        uploadedFile: any
    ) => {
        if (!documentTypeName || !uploadedFile) {
            return;
        }

        try {
            const actualFile = uploadedFile.originFile || uploadedFile;

            if (!(actualFile instanceof Blob)) {
                showToast(
                    'Error',
                    'Invalid file format. Please upload a valid Excel file.',
                    'error'
                );
                return;
            }

            const formData = new FormData();
            formData.append('DocumentTypeName', documentTypeName);
            formData.append('File', actualFile);

            const token = auth.user?.id_token;

            if (!token) {
                throw new Error('No ID token available');
            }

            setCreateDocTypeModalVisible(false);

            await axios.post('/v1/FillPdf/Create', formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                baseURL: process.env.REACT_APP_API_BASE_URL || '',
            });

            showToast(
                'Success',
                `Document type "${documentTypeName}" created successfully`,
                'success'
            );

            fetchDocumentTypes();
        } catch (error: any) {
            showToast(
                'Error',
                `Failed to create document type: ${
                    error.message || 'Unknown error'
                }`,
                'error'
            );
            setCreateDocTypeModalVisible(false);
        }
    };

    return (
        <div className="fill-pdf-settings">
            <div className="header">
                <h2 className="ap-typography-heading-m title">
                    Fill PDF Document Types
                </h2>
                <Search
                    className="search-docs"
                    searchType="secondary"
                    placeholder="Search document types..."
                    value={searchTerm}
                    onChange={(value: string) => setSearchTerm(value)}
                />
            </div>
            <div className="table-container">
                {filteredDocTypes.length > 0 ? (
                    <Table
                        className="extraction-table"
                        originalData={filteredDocTypes}
                        pageSize={pageSize}
                        hasTitle
                    >
                        <Column
                            field="documentTypeName"
                            renderCell={(row) => (
                                <div className="d-flex align-items-center gap-2">
                                    <span
                                        className="Appkit4-icon icon-view-outline"
                                        role="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleExpand(row);
                                        }}
                                    />
                                    <span>{row.documentTypeName}</span>
                                </div>
                            )}
                        >
                            Document type
                        </Column>
                        <Column field="documentTypeId" renderCell={renderCell}>
                            Document type id
                        </Column>
                        <Column
                            field="documentTypeName"
                            renderCell={(row: DocumentType) => (
                                <span
                                    className="Appkit4-icon icon-delete-outline"
                                    onClick={() =>
                                        handleDeleteTemplate(row.documentTypeId)
                                    }
                                    style={{
                                        marginLeft: '66px',
                                        cursor: 'pointer',
                                    }}
                                ></span>
                            )}
                        >
                            Delete document type
                        </Column>
                        <Column
                            field="documentTypeName"
                            style={{ width: '50px' }}
                            renderCell={(row: DocumentType) => (
                                <span
                                    className="Appkit4-icon icon-enter-right-outline action-icon"
                                    role="button"
                                    onClick={() => navigateToSettings(row)}
                                    aria-label="Settings"
                                />
                            )}
                        />
                    </Table>
                ) : (
                    <div>No document types yet</div>
                )}
            </div>
            <Button
                className="create-template-button"
                icon="icon-circle-plus-outline"
                style={{ marginTop: '20px' }}
                onClick={handleCreateDocType}
            >
                Create document type
            </Button>

            <DeleteModal
                visible={deleteModalVisible}
                onClose={handleCancelDelete}
                title="Delete Document Type"
                content={
                    <div>
                        <p>
                            Are you sure you want to delete the document type
                            &quot;{documentTypeToDeleteName}&quot;?
                        </p>
                        <p>This action cannot be undone.</p>
                        <div className="delete-modal-actions">
                            <button
                                className="ap-button ap-button-secondary"
                                onClick={handleCancelDelete}
                            >
                                Cancel
                            </button>
                            <button
                                className="ap-button ap-button-primary ap-button-danger"
                                onClick={handleConfirmDelete}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                }
            />
            {selectedDocument && (
                <PDFTemplateModal
                    visible={isPdfModalVisible}
                    onClose={() => {
                        setIsPdfModalVisible(false);
                        setSelectedDocument(null);
                    }}
                    documentTypeId={selectedDocument}
                />
            )}
            <CreateDocTypeModal
                visible={createDocTypeModalVisible}
                onClose={handleCloseCreateDocTypeModal}
                onConfirm={handleConfirmCreateDocTypeModal}
                documentTypes={availableDocTypes}
            />
        </div>
    );
};

export default FillPDFSettings;
